import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  increment,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import "./CommentSection.css";

const formatTimestamp = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return "";
    return new Intl.DateTimeFormat(undefined, {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  } catch {
    return "";
  }
};

const sanitize = (v) => {
  try {
    let s = String(v ?? "");
    // Normalize newlines to spaces
    s = s.replace(/[\r\n]+/g, " ");
    // Normalize various dash characters
    s = s.replace(/[\u2012-\u2015]/g, " - ");
    // Remove only control characters (keep full Unicode, including emoji)
    s = s.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
    // Collapse excessive whitespace
    s = s.replace(/\s{2,}/g, " ").trim();
    return s;
  } catch {
    return v;
  }
};

const getDisplayName = (profile = {}, user = null) => {
  if (profile.rank || profile.firstInitialLastName || profile.agency) {
    return [
      profile.rank,
      profile.firstInitialLastName || profile.displayId,
      profile.agency ? `- ${profile.agency}` : "",
    ].filter(Boolean).join(" ");
  }
  return user?.displayName || user?.email || "User";
};

const isModerationHidden = (item) =>
  item?.isHidden === true || item?.authorDeleted === true;

const CommentSection = ({
  postId,
  post = null,
  hideHeader = false,
  showInput = true,
  onSubmitComplete,
  initiallyExpanded = false,
  isAdmin = false,
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [privacy, setPrivacy] = useState("real");
  const [replyText, setReplyText] = useState({});
  const [activeReply, setActiveReply] = useState(null);
  const [postMeta, setPostMeta] = useState(post || null);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(null); // { kind: 'comment'|'reply', commentId, replyId?, text }
  const [moderationBusy, setModerationBusy] = useState("");

  // Live list of comments
  useEffect(() => {
    const q = query(collection(db, "posts", postId, "comments"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setComments(list);
    });
    return () => unsubscribe();
  }, [postId]);

  // Fetch post metadata for comment lock state.
  useEffect(() => {
    const fetchPostAuthor = async () => {
      const postSnap = await getDoc(doc(db, "posts", postId));
      if (postSnap.exists()) {
        setPostMeta({ id: postSnap.id, ...postSnap.data() });
      }
    };
    fetchPostAuthor();
  }, [postId]);

  useEffect(() => {
    if (post) setPostMeta(post);
  }, [post]);

  // Fetch current user's profile for dropdown labels
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setProfile(snap.data());
    };
    run();
  }, []);

  const handleSubmit = async (e, parentId = null) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user) return;
    if (postMeta?.commentsLocked === true && !isAdmin) {
      alert("Comments have been locked by moderation.");
      return;
    }

    const content = parentId ? replyText[parentId] : newComment;
    if (!content?.trim()) return;

    const profileRef = doc(db, "users", user.uid);
    const profileSnap = await getDoc(profileRef);
    const p = profileSnap.data() || {};

    // Anonymous comment limit: 1 / day
    if (privacy === "anonymous") {
      const lastAnon = p?.lastAnonymousComment?.toDate?.();
      if (lastAnon && Date.now() - lastAnon.getTime() < 24 * 60 * 60 * 1000) {
        alert("You can post one anonymous comment per day.");
        return;
      }
      await updateDoc(profileRef, { lastAnonymousComment: serverTimestamp() });
    }

    const commentData = {
      postId,
      userId: user.uid,
      userName: p?.firstInitialLastName || `${(p.firstName || "").slice(0, 1)}. ${p.lastName || "Officer"}`.trim(),
      rank: p?.rank || "",
      agency: p?.agency || "",
      text: content.trim(),
      privacyTier: privacy,
      createdAt: serverTimestamp(),
      isHidden: false,
      authorDeleted: false,
    };

    if (parentId) {
      await addDoc(collection(db, "posts", postId, "comments", parentId, "replies"), commentData);
    } else {
      await addDoc(collection(db, "posts", postId, "comments"), commentData);
      await updateDoc(doc(db, "posts", postId), { commentCount: increment(1) });
    }

    if (parentId) setReplyText((prev) => ({ ...prev, [parentId]: "" }));
    else setNewComment("");
    setPrivacy("real");
    setActiveReply(null);
    if (onSubmitComplete) onSubmitComplete();
  };

  const displayComments = comments.filter((comment) =>
    isAdmin || !isModerationHidden(comment)
  );
  const visibleComments = expanded ? displayComments : displayComments.slice(0, 1);
  const hiddenCount = Math.max(0, displayComments.length - visibleComments.length);
  const commentsLocked = postMeta?.commentsLocked === true;
  const canAddComments = showInput && (!commentsLocked || isAdmin);

  const getActorMeta = () => ({
    uid: auth.currentUser?.uid || "",
    name: getDisplayName(profile, auth.currentUser),
  });

  const handleHideItem = async (kind, commentId, replyId = null) => {
    const busyKey = `${kind}:${replyId || commentId}:hide`;
    const actor = getActorMeta();
    const ref = kind === "comment"
      ? doc(db, "posts", postId, "comments", commentId)
      : doc(db, "posts", postId, "comments", commentId, "replies", replyId);
    setModerationBusy(busyKey);
    try {
      await updateDoc(ref, {
        isHidden: true,
        hiddenBy: actor.uid,
        hiddenByName: actor.name,
        hiddenReason: "Hidden by admin",
        hiddenAt: serverTimestamp(),
      });
    } finally {
      setModerationBusy("");
    }
  };

  const handleRestoreItem = async (item, kind, commentId, replyId = null) => {
    if (item.authorDeleted === true) {
      alert("Cannot restore content deleted by the author.");
      return;
    }
    const ref = kind === "comment"
      ? doc(db, "posts", postId, "comments", commentId)
      : doc(db, "posts", postId, "comments", commentId, "replies", replyId);
    const busyKey = `${kind}:${replyId || commentId}:restore`;
    setModerationBusy(busyKey);
    try {
      await updateDoc(ref, {
        isHidden: false,
        hiddenBy: "",
        hiddenByName: "",
        hiddenReason: "",
        hiddenAt: null,
      });
    } finally {
      setModerationBusy("");
    }
  };

  const deleteCommentOrReply = async ({ kind, commentId, replyId = null, item }) => {
    const user = auth.currentUser;
    if (!user) return;
    const ownerDeletingHidden =
      kind === "comment" &&
      user.uid === item?.userId &&
      item?.isHidden === true &&
      !isAdmin;
    if (ownerDeletingHidden) {
      await updateDoc(doc(db, "posts", postId, "comments", commentId), {
        authorDeleted: true,
        authorDeletedAt: serverTimestamp(),
        authorDeletedBy: user.uid,
        authorDeletedByName: getDisplayName(profile, user),
      });
      return;
    }

    const busyKey = `${kind}:${replyId || commentId}:delete`;
    setModerationBusy(busyKey);
    try {
      if (kind === "comment") {
        const rSnap = await getDocs(collection(db, 'posts', postId, 'comments', commentId, 'replies'));
        await Promise.all(rSnap.docs.map((d) => deleteDoc(d.ref)));
        await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
        await updateDoc(doc(db, 'posts', postId), { commentCount: increment(-1) });
      } else {
        const replyRef = doc(db, 'posts', postId, 'comments', commentId, 'replies', replyId);
        const ownerDeletingHiddenReply =
          user.uid === item?.userId && item?.isHidden === true && !isAdmin;
        if (ownerDeletingHiddenReply) {
          await updateDoc(replyRef, {
            authorDeleted: true,
            authorDeletedAt: serverTimestamp(),
            authorDeletedBy: user.uid,
            authorDeletedByName: getDisplayName(profile, user),
          });
          return;
        }
        await deleteDoc(replyRef);
      }
    } finally {
      setModerationBusy("");
    }
  };

  const nameInitialLast = (p) => {
    if (!p) return "Officer";
    if (p.firstName && p.lastName) return `${p.firstName[0]}. ${p.lastName}`;
    if (p.firstInitialLastName) return p.firstInitialLastName.includes(".") ? p.firstInitialLastName.replace(".", ". ") : p.firstInitialLastName;
    return p.displayId || "Officer";
  };
  const userAgency = sanitize(profile?.agency || "");
  const userRank = sanitize(profile?.rank || "Officer");
  const userIL = sanitize(nameInitialLast(profile));
  const optReal = `${userRank} ${userIL}${userAgency ? ` - ${userAgency}` : ""}`;
  const optUserDept = `${userIL}${userAgency ? ` - ${userAgency}` : ""}`;
  const optAgencyOnly = `${userAgency}`;

  return (
    <div className="comment-section">
      {!hideHeader && <h4>Comments</h4>}

      {visibleComments.map((c) => (
        <div
          key={c.id}
          className={`comment-card ${c.privacyTier === "anonymous" ? "anonymous-comment" : ""}`}
        >
          <div className="comment-header">
            {c.privacyTier === "anonymous" ? (
              "Anonymous Officer"
            ) : c.privacyTier === "agencyOnly" ? (
              <span className="identity">
                <span className="agency">{sanitize(c.agency)}</span>
              </span>
            ) : c.privacyTier === "usernameDept" ? (
              <span className="identity">
                <span className="name"><strong>{sanitize(c.userName)}</strong></span>
                {c.agency ? (
                  <span className="agency">{` - ${sanitize(c.agency)}`}</span>
                ) : null}
              </span>
            ) : (
              <span className="identity">
                <span className="name">
                  {c.rank && `${sanitize(c.rank)} `}
                  <strong>{sanitize(c.userName)}</strong>
                </span>
                {c.agency ? (
                  <span className="agency">{` - ${sanitize(c.agency)}`}</span>
                ) : null}
              </span>
            )}
            <span className="comment-timestamp">{formatTimestamp(c.createdAt)}</span>
            {isAdmin && c.isHidden === true && (
              <span className="moderation-badge">Hidden by moderation</span>
            )}
            {isAdmin && c.authorDeleted === true && (
              <span className="moderation-badge author-deleted">Deleted by author</span>
            )}
            {auth.currentUser?.uid === c.userId && !isModerationHidden(c) && (
              <button
                title="Edit comment"
                style={{ marginLeft: 8, border: 'none', background: 'transparent', color: '#007bff', cursor: 'pointer' }}
                onClick={() => setEditing({ kind: 'comment', commentId: c.id, text: c.text || '' })}
              >
                ✎
              </button>
            )}
          </div>

          <div className="comment-body">{sanitize(c.text)}</div>

          {isAdmin && (
            <div className="comment-moderation-actions">
              {c.isHidden === true ? (
                c.authorDeleted !== true ? (
                  <button
                    type="button"
                    disabled={moderationBusy === `comment:${c.id}:restore`}
                    onClick={() => handleRestoreItem(c, "comment", c.id)}
                  >
                    {moderationBusy === `comment:${c.id}:restore` ? "Restoring..." : "Restore"}
                  </button>
                ) : null
              ) : (
                <button
                  type="button"
                  disabled={moderationBusy === `comment:${c.id}:hide`}
                  onClick={() => handleHideItem("comment", c.id)}
                >
                  {moderationBusy === `comment:${c.id}:hide` ? "Hiding..." : "Hide"}
                </button>
              )}
              <button
                type="button"
                className="danger"
                disabled={moderationBusy === `comment:${c.id}:delete`}
                onClick={() => {
                  if (window.confirm("Delete this comment permanently?")) {
                    deleteCommentOrReply({ kind: "comment", commentId: c.id, item: c });
                  }
                }}
              >
                {moderationBusy === `comment:${c.id}:delete` ? "Deleting..." : "Delete"}
              </button>
            </div>
          )}

          {auth.currentUser && (!commentsLocked || isAdmin) && !isModerationHidden(c) && (
            <button
              className="reply-button"
              onClick={() => setActiveReply(activeReply === c.id ? null : c.id)}
            >
              {activeReply === c.id ? "Cancel" : "Reply"}
            </button>
          )}

          <div className="reply-block">
            <Replies
              postId={postId}
              commentId={c.id}
              isAdmin={isAdmin}
              onEdit={(reply) => setEditing({ kind: 'reply', commentId: c.id, replyId: reply.id, text: reply.text || '' })}
              onHide={(reply) => handleHideItem("reply", c.id, reply.id)}
              onRestore={(reply) => handleRestoreItem(reply, "reply", c.id, reply.id)}
              onDelete={(reply) => {
                if (window.confirm("Delete this reply permanently?")) {
                  deleteCommentOrReply({
                    kind: "reply",
                    commentId: c.id,
                    replyId: reply.id,
                    item: reply,
                  });
                }
              }}
              moderationBusy={moderationBusy}
            />
          </div>

          {activeReply === c.id && (!commentsLocked || isAdmin) && (
            <form onSubmit={(e) => handleSubmit(e, c.id)}>
              <textarea
                placeholder="Write a reply..."
                value={replyText[c.id] || ""}
                onChange={(e) => setReplyText((prev) => ({ ...prev, [c.id]: e.target.value }))}
                rows={2}
              />
              <button type="submit">Send</button>
            </form>
          )}
        </div>
      ))}

      {hiddenCount > 0 && !expanded && (
        <button className="toggle-comments" onClick={() => setExpanded(true)}>
          See more comments ({hiddenCount})
        </button>
      )}
      {expanded && displayComments.length > 1 && (
        <button className="toggle-comments" onClick={() => setExpanded(false)}>
          Hide comments
        </button>
      )}

      {commentsLocked && !isAdmin ? (
        <div className="comments-locked-message">
          Comments have been locked by moderation.
        </div>
      ) : null}

      {canAddComments && (
        <form className="comment-form" onSubmit={handleSubmit}>
          <textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={2}
          />
          <div className="comment-options">
            <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
              <option value="real">{optReal}</option>
              <option value="usernameDept">{optUserDept}</option>
              <option value="agencyOnly">{optAgencyOnly}</option>
              <option value="anonymous">Anonymous</option>
            </select>
            <button type="submit">Comment</button>
          </div>
        </form>
      )}
            {editing && ReactDOM.createPortal(
        (
          <div className="comment-edit-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2147483000 }} onClick={() => setEditing(null)}>
            <div className="comment-edit-modal" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: 12, width: '92%', maxWidth: 560, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
              <h4 style={{ margin: 0, marginBottom: 8 }}>Edit {editing.kind === 'comment' ? 'Comment' : 'Reply'}</h4>
              <textarea rows={4} style={{ width: '100%', minHeight: 120, border: '1px solid #cbd5e1', borderRadius: 8, padding: 8 }} value={editing.text} onChange={(e) => setEditing((prev) => ({ ...prev, text: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    try {
                      const newText = sanitize(editing.text || '');
                      if (!newText) { alert('Text cannot be empty.'); return; }
                      const targetItem = editing.kind === 'comment'
                        ? comments.find((comment) => comment.id === editing.commentId)
                        : null;
                      if (targetItem && isModerationHidden(targetItem)) {
                        alert("Cannot edit content hidden by moderation.");
                        return;
                      }
                      if (editing.kind === 'comment') {
                        await updateDoc(doc(db, 'posts', postId, 'comments', editing.commentId), { text: newText, editedAt: serverTimestamp() });
                      } else {
                        await updateDoc(doc(db, 'posts', postId, 'comments', editing.commentId, 'replies', editing.replyId), { text: newText, editedAt: serverTimestamp() });
                      }
                      setEditing(null);
                    } catch (e) {
                      console.error('Failed to save edit', e);
                      alert('Could not save changes.');
                    }
                  }}
                  style={{ background: '#007bff', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                >
                  Save
                </button>
              <button
                onClick={() => setEditing(null)}
                style={{ background: '#e5e7eb', color: '#111827', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
              >
                Cancel
              </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Delete this ' + (editing.kind === 'comment' ? 'comment' : 'reply') + '?')) return;
                    try {
                      await deleteCommentOrReply({
                        kind: editing.kind,
                        commentId: editing.commentId,
                        replyId: editing.replyId,
                        item: editing.kind === 'comment'
                          ? comments.find((comment) => comment.id === editing.commentId)
                          : {},
                      });
                      setEditing(null);
                    } catch (e) {
                      console.error('Failed to delete', e);
                      alert('Could not delete.');
                    }
                  }}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ), document.body)}
    </div>
  );
};

const Replies = ({
  postId,
  commentId,
  onEdit,
  isAdmin = false,
  onHide,
  onRestore,
  onDelete,
  moderationBusy = "",
}) => {
  const [replies, setReplies] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "posts", postId, "comments", commentId, "replies"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setReplies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [postId, commentId]);

  const displayReplies = replies.filter((reply) =>
    isAdmin || !isModerationHidden(reply)
  );
  const visibleReplies = expanded ? displayReplies : displayReplies.slice(0, 1);
  const hiddenReplyCount = Math.max(0, displayReplies.length - visibleReplies.length);

  return (
    <>
      {visibleReplies.map((r) => (
        <div key={r.id} className={`comment-card reply ${r.privacyTier === "anonymous" ? "anonymous-comment" : ""}`}>
          <div className="comment-header">
            {r.privacyTier === "anonymous" ? (
              "Anonymous Officer"
            ) : r.privacyTier === "agencyOnly" ? (
              <span className="identity">
                <span className="agency">{sanitize(r.agency)}</span>
              </span>
            ) : r.privacyTier === "usernameDept" ? (
              <span className="identity">
                <span className="name"><strong>{sanitize(r.userName)}</strong></span>
                {r.agency ? (
                  <span className="agency">{` - ${sanitize(r.agency)}`}</span>
                ) : null}
              </span>
            ) : (
              <span className="identity">
                <span className="name">
                  {r.rank && `${sanitize(r.rank)} `}
                  <strong>{sanitize(r.userName)}</strong>
                </span>
                {r.agency ? (
                  <span className="agency">{` - ${sanitize(r.agency)}`}</span>
                ) : null}
              </span>
            )}
            <span className="comment-timestamp">{formatTimestamp(r.createdAt)}</span>
            {isAdmin && r.isHidden === true && (
              <span className="moderation-badge">Hidden by moderation</span>
            )}
            {isAdmin && r.authorDeleted === true && (
              <span className="moderation-badge author-deleted">Deleted by author</span>
            )}
            {auth.currentUser?.uid === r.userId && !isModerationHidden(r) && (
              <button
                title="Edit reply"
                style={{ marginLeft: 8, border: 'none', background: 'transparent', color: '#007bff', cursor: 'pointer' }}
                onClick={() => onEdit?.(r)}
              >✎</button>
            )}
          </div>
          <div className="comment-body">{sanitize(r.text)}</div>
          {isAdmin && (
            <div className="comment-moderation-actions">
              {r.isHidden === true ? (
                r.authorDeleted !== true ? (
                  <button
                    type="button"
                    disabled={moderationBusy === `reply:${r.id}:restore`}
                    onClick={() => onRestore?.(r)}
                  >
                    {moderationBusy === `reply:${r.id}:restore` ? "Restoring..." : "Restore"}
                  </button>
                ) : null
              ) : (
                <button
                  type="button"
                  disabled={moderationBusy === `reply:${r.id}:hide`}
                  onClick={() => onHide?.(r)}
                >
                  {moderationBusy === `reply:${r.id}:hide` ? "Hiding..." : "Hide"}
                </button>
              )}
              <button
                type="button"
                className="danger"
                disabled={moderationBusy === `reply:${r.id}:delete`}
                onClick={() => onDelete?.(r)}
              >
                {moderationBusy === `reply:${r.id}:delete` ? "Deleting..." : "Delete"}
              </button>
            </div>
          )}
        </div>
      ))}
      {hiddenReplyCount > 0 && !expanded && (
        <button
          type="button"
          className="toggle-replies"
          onClick={() => setExpanded(true)}
        >
          See more replies ({hiddenReplyCount})
        </button>
      )}
      {expanded && displayReplies.length > 1 && (
        <button
          type="button"
          className="toggle-replies"
          onClick={() => setExpanded(false)}
        >
          Hide replies
        </button>
      )}
    </>
  );
};

export default CommentSection;









